import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  isOnFoldersView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  fetchAllConversations: async ({ commit, state, dispatch }) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const params = state.conversationFilters;
      const {
        data: { data },
      } = await ConversationApi.get(params);
      // NOTICE: KAPTION-INTEGRATION - The "unread" tab sends
      // `assigneeType=all + conversationType=unread` so the backend's
      // ConversationFinder applies the unread scope without double-scoping
      // by assignee. But that means `params.assigneeType` here is "all",
      // while ChatList reads pagination state keyed by activeAssigneeTab
      // ("unread"). Without aligning the key, the writer increments
      // `currentPage.all` and the reader keeps seeing `currentPage.unread = 0`
      // — pagination is stuck on page 1, infinite-scroll fires forever
      // (`page=NaN` before the store key existed, `page=1` once it did).
      // Use the conversationType when present so reader and writer share
      // the same key.
      const filterType =
        params.conversationType === 'unread' ? 'unread' : params.assigneeType;
      buildConversationList({ commit, dispatch }, params, data, filterType);
    } catch (error) {
      // Handle error
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      // Handle error
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED);
    if (data.dataFetched === undefined) {
      try {
        await dispatch('fetchPreviousMessages', {
          after,
          before: data.messages[0].id,
          conversationId: data.id,
        });
        data.dataFetched = true;
      } catch (error) {
        // Ignore error
      }
    }
  },

  assignAgent: async ({ dispatch }, { conversationId, agentId }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setCurrentChatAssignee', response.data);
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee({ commit }, assignee) {
    commit(types.ASSIGN_AGENT, assignee);
  },

  // NOTICE: KAPTION-INTEGRATION
  assignAssistant: async ({ dispatch }, { conversationId, assistantId }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId: assistantId,
      });
      dispatch('setCurrentChatAssistant', response.data);
    } catch (error) {
      // Handle error
    }
  },

  // NOTICE: KAPTION-INTEGRATION
  setCurrentChatAssistant({ commit }, assistant) {
    commit(types.ASSIGN_AGENT, assistant);
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null }
  ) => {
    try {
      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
  },

  updateMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
  },

  editMessage: async ({ commit }, { conversationId, messageId, content }) => {
    const { data } = await MessageApi.update(
      conversationId,
      messageId,
      content
    );
    commit(types.ADD_MESSAGE, data);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteConversation: async ({ commit, dispatch }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      dispatch('conversationStats/get', {}, { root: true });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch }, conversation) {
    const {
      meta: { sender },
    } = conversation;
    commit(types.UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  // NOTICE: KAPTION-INTEGRATION - Groups only filter action
  setGroupsOnlyFilter({ commit }, data) {
    commit(types.CHANGE_GROUPS_ONLY_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async (
    { commit },
    { conversationId, mutedUntil = null }
  ) => {
    try {
      await ConversationApi.mute(conversationId, mutedUntil);
      commit(types.MUTE_CONVERSATION, { conversationId, mutedUntil });
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION, conversationId);
    } catch (error) {
      //
    }
  },

  // SID: conversation-pinning.PIN_TOGGLE.4
  pinConversation: async ({ commit }, { conversationId }) => {
    try {
      await ConversationApi.pin(conversationId);
      commit(types.PIN_CONVERSATION, {
        conversationId,
        pinnedAt: new Date().toISOString(),
      });
    } catch (error) {
      //
    }
  },

  // SID: conversation-pinning.PIN_TOGGLE.4
  unpinConversation: async ({ commit }, { conversationId }) => {
    try {
      await ConversationApi.unpin(conversationId);
      commit(types.UNPIN_CONVERSATION, conversationId);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
    } catch (error) {
      // Handle error
    }
  },

  updateAdditionalAttributes: async (
    { commit },
    { conversationId, additionalAttributes }
  ) => {
    try {
      await ConversationApi.updateAdditionalAttributes({
        conversationId,
        additionalAttributes,
      });
      // Update the conversation in the store
      commit(types.UPDATE_CONVERSATION_ADDITIONAL_ATTRIBUTES, {
        conversationId,
        additionalAttributes,
      });
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxCaptainAssistantById: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.getInboxAssistant(conversationId);
      commit(types.SET_INBOX_CAPTAIN_ASSISTANT, response.data);
    } catch (error) {
      // Handle error
    }
  },

  // NOTICE: KAPTION-INTEGRATION - Add logging for reaction toggle
  toggleReaction: async (
    { commit },
    { conversationId, messageId, reaction }
  ) => {
    try {
      // NOTICE: KAPTION-INTEGRATION - Send reaction to backend API
      const { data } = await MessageApi.react(
        conversationId,
        messageId,
        reaction
      );
      commit(types.ADD_MESSAGE, data);
    } catch (error) {
      // Report to Sentry if available
      if (Sentry && typeof Sentry.captureException === 'function') {
        Sentry.captureException(error, {
          tags: {
            feature: 'reactions',
            action: 'toggleReaction',
          },
          contexts: {
            reaction: {
              conversationId,
              messageId,
              reaction,
            },
          },
        });
      }
      throw new Error(error);
    }
  },

  // NOTICE: KAPTION-INTEGRATION - Undo send feature actions
  /**
   * Creates a message with undo capability - message appears in chat but isn't sent after configured delay
   * @param {Object} data - Message data (conversationId, content, attachments, etc.)
   * @returns {Object} { tempId, undo: Function } - tempId and undo function to cancel
   */
  createMessageWithUndo: async ({ commit, dispatch, rootGetters }, data) => {
    const pendingMessage = createPendingMessage(data);
    const tempId = pendingMessage.id;
    const conversationId = pendingMessage.conversation_id;
    // Get duration from ui_settings, default to 15 seconds
    const uiSettings = rootGetters.getUISettings || {};
    const UNDO_DURATION = uiSettings.kaption_undo_send_duration || 15;

    // NOTICE: KAPTION-INTEGRATION - Extract original untranslated content for undo restoration
    // When live translation is active, the message content is translated but the original
    // is stored in contentAttributes.kaption_translations[sourceLanguage].text
    // We need to preserve this so undo restores the original text the agent typed
    const translations = data.contentAttributes?.kaption_translations;
    let originalUntranslatedContent = null;
    if (translations) {
      // The original message is stored under the agent's source language key
      const translationEntry = Object.values(translations)[0];
      if (translationEntry?.text) {
        originalUntranslatedContent = translationEntry.text;
      }
    }

    // eslint-disable-next-line no-console
    console.log('[KAPTION] createMessageWithUndo - translation extraction:', {
      hasContentAttributes: !!data.contentAttributes,
      hasKaptionTranslations: !!translations,
      translationKeys: translations ? Object.keys(translations) : [],
      originalUntranslatedContent,
      messageContent: data.message,
    });

    // Add message to chat with pending_undo status
    commit(types.ADD_MESSAGE, {
      ...pendingMessage,
      status: MESSAGE_STATUS.PENDING_UNDO,
    });

    // Store undo pending data with timer
    const timerId = setTimeout(() => {
      dispatch('confirmUndoMessage', tempId);
    }, UNDO_DURATION * 1000);

    // Store countdown interval for UI updates
    let remainingSeconds = UNDO_DURATION;
    const countdownInterval = setInterval(() => {
      remainingSeconds -= 1;
      if (remainingSeconds >= 0) {
        commit(types.UPDATE_UNDO_PENDING_COUNTDOWN, {
          tempId,
          remainingSeconds,
        });
      }
      if (remainingSeconds <= 0) {
        clearInterval(countdownInterval);
      }
    }, 1000);

    commit(types.ADD_UNDO_PENDING_MESSAGE, {
      tempId,
      data: {
        timerId,
        countdownInterval,
        payload: pendingMessage,
        // NOTICE: KAPTION-INTEGRATION - Store original content separately for undo restoration
        originalUntranslatedContent,
        conversationId,
        remainingSeconds: UNDO_DURATION,
        createdAt: Date.now(),
      },
    });

    return {
      tempId,
      undo: () => dispatch('undoMessage', tempId),
    };
  },

  /**
   * Undoes a pending message - cancels the send and returns content for editing
   * @param {string} tempId - The temporary message ID
   * @returns {Object|null} The original message payload for restoring to composer
   */
  undoMessage: ({ commit, state }, tempId) => {
    const pending = state.undoPendingMessages[tempId];
    if (!pending) return null;

    // Clear the timers
    clearTimeout(pending.timerId);
    if (pending.countdownInterval) {
      clearInterval(pending.countdownInterval);
    }

    // Remove from chat UI - find and remove the message from conversation
    const conversationId = pending.conversationId;
    const conversation = state.allConversations.find(
      c => c.id === conversationId
    );
    if (conversation) {
      conversation.messages = conversation.messages.filter(
        m => m.id !== tempId
      );
    }

    // Remove from undo pending state
    commit(types.REMOVE_UNDO_PENDING_MESSAGE, tempId);

    // NOTICE: KAPTION-INTEGRATION - Return payload with original untranslated content
    // If live translation was active, we stored the original text separately
    // Override the content field so the ReplyBox restores what the agent typed
    const payload = { ...pending.payload };

    // eslint-disable-next-line no-console
    console.log('[KAPTION] undoMessage - restoring content:', {
      hasOriginalUntranslatedContent: !!pending.originalUntranslatedContent,
      originalUntranslatedContent: pending.originalUntranslatedContent,
      payloadContentBefore: pending.payload?.content,
    });

    if (pending.originalUntranslatedContent) {
      payload.content = pending.originalUntranslatedContent;
    }

    // eslint-disable-next-line no-console
    console.log(
      '[KAPTION] undoMessage - final payload content:',
      payload.content
    );

    return payload;
  },

  /**
   * Confirms and actually sends a pending undo message (called when timer expires)
   * @param {string} tempId - The temporary message ID
   */
  confirmUndoMessage: async ({ commit, state, dispatch }, tempId) => {
    const pending = state.undoPendingMessages[tempId];
    if (!pending) return;

    // Clear countdown interval if still running
    if (pending.countdownInterval) {
      clearInterval(pending.countdownInterval);
    }

    // Remove from undo pending state
    commit(types.REMOVE_UNDO_PENDING_MESSAGE, tempId);

    // Actually send the message
    const { payload } = pending;
    dispatch('sendMessageWithData', payload);
  },

  /**
   * Undoes the most recent pending message (for hotkey support)
   * @returns {Object|null} The original message payload
   */
  undoLastMessage: ({ state, dispatch }) => {
    const entries = Object.entries(state.undoPendingMessages);
    if (entries.length === 0) return null;

    // Find the most recent one
    const sorted = entries.sort(
      (a, b) => (b[1].createdAt || 0) - (a[1].createdAt || 0)
    );
    const [tempId] = sorted[0];

    return dispatch('undoMessage', tempId);
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
